<template lang="pug">
    .table-timeline-panel
        .table-timeline-panel__header
            .table-timeline-panel__title
                strong Tisch {{ table.number }}
                span.table-timeline-panel__seats  · {{ table.seats }} Plätze
            el-button(type="text" icon="el-icon-close" size="mini" @click="$emit('close')")

        el-date-picker(
            v-model="localDate"
            type="date"
            format="dd.MM.yyyy"
            value-format="yyyy-MM-dd"
            :clearable="false"
            size="mini"
            style="width: 100%; margin-bottom: 10px"
        )

        .table-timeline-panel__timeline(ref="timelineBody")
            .table-timeline-panel__slot-row(
                v-for="slot in timeSlots"
                :key="slot"
                :class="{ 'table-timeline-panel__slot-row--hour': isFullHour(slot), 'table-timeline-panel__slot-row--now': isCurrentSlotLocal(slot) }"
                @dragover.prevent="onSlotDragOver(slot, $event)"
                @dragleave="onSlotDragLeave"
                @drop.prevent="onSlotDrop(slot, $event)"
            )
                .table-timeline-panel__slot-label {{ isFullHour(slot) ? slot : '' }}
                .table-timeline-panel__slot-area(
                    :class="{ 'table-timeline-panel__slot-area--drag-over': dragOverSlot === slot }"
                    @click="showSlotMenu(slot, $event)"
                )

            //- Reservation blocks (absolutely positioned over the slots)
            .table-timeline-panel__reservation(
                v-for="res in tableReservations"
                :key="res.id"
                :class="'table-timeline-panel__reservation--' + res.status"
                :style="getReservationStyle(res)"
                :draggable="canUpdateReservation"
                @dragstart="onResDragStart(res, $event)"
                @dragend="onResDragEnd"
                @click.stop="$emit('edit-reservation', res)"
            )
                .table-timeline-panel__res-time {{ res.time }}
                .table-timeline-panel__res-guest {{ res.guestName || 'Walk-in' }}
                .table-timeline-panel__res-size {{ res.partySize }} Pers.

        .table-timeline-panel__slot-menu(
            v-if="slotMenu.visible"
            :style="{ left: slotMenu.x + 'px', top: slotMenu.y + 'px' }"
            @click.stop
        )
            .table-timeline-panel__slot-menu-header
                span {{ slotMenu.slot }} · Tisch {{ table.number }}
                el-button(type="text" icon="el-icon-close" size="mini" @click="closeSlotMenu")
            .table-timeline-panel__slot-menu-actions
                el-button(v-if="canCreateReservation" type="primary" size="small" @click="quickSeat") Sofort belegen
                el-button(v-if="canCreateReservation" size="small" @click="openNewReservationForm") Neue Reservierung
</template>

<script>
import { format } from 'date-fns';
import { checkRestrictions } from '@/helpers';
import { useTableReservationStore } from '@/stores';
import timelineMixin, { TIMELINE_SLOT_MINUTES } from '@/extensions/tableReservation/views/composables/timelineMixin';
import router from '@/router';

const ROW_HEIGHT = 32; // px per 30-min slot

export default {
    name: 'TableTimelinePanel',
    mixins: [timelineMixin],
    props: {
        table: {
            type: Object,
            required: true
        },
        date: {
            type: String,
            default: null
        }
    },
    data: function () {
        return {
            localDate: this.date || format(new Date(), 'yyyy-MM-dd'),
            dragOverSlot: null,
            slotMenu: { visible: false, slot: null, x: 0, y: 0 },
            slotDismissHandler: null
        };
    },
    computed: {
        reservationStore: function () {
            return useTableReservationStore();
        },
        canCreateReservation: function () {
            return checkRestrictions(['ex:trsv:reservations'], 'create');
        },
        canUpdateReservation: function () {
            return checkRestrictions(['ex:trsv:reservations'], 'update');
        },
        tableReservations: function () {
            return this.reservationStore.reservationsByTableAndDate(this.table.id, this.localDate);
        },
        isShowingToday: function () {
            return this.isDateToday(this.localDate);
        }
    },
    watch: {
        date: function (val) {
            if (val) this.localDate = val;
        },
        localDate: function (val) {
            if (val && val !== this.reservationStore.filters.date) {
                this.reservationStore.setFilterDate(val).catch((error) => {
                    this.showActionError(error);
                });
            }
        }
    },
    mounted: function () {
        // currentTime tick is provided by timelineMixin
    },
    beforeDestroy: function () {
        if (this.slotDismissHandler) document.removeEventListener('click', this.slotDismissHandler);
    },
    methods: {
        pad: function (n) {
            return this.padTime(n);
        },
        isCurrentSlotLocal: function (slot) {
            return this.isCurrentSlot(slot, this.localDate);
        },
        toMins: function (time) {
            return this.timeToMinutes(time);
        },
        showActionError: function (error) {
            this.$message({
                message: error.message || this.$t('Something went wrong'),
                type: 'error',
                duration: 5000
            });
        },
        getReservationStyle: function (res) {
            const idx = this.slotIndex(res.time);
            const top = idx * ROW_HEIGHT;
            const { durationMins } = this.getReservationDurationMinutes(res, this.tableReservations, this.localDate);
            const height = (durationMins / TIMELINE_SLOT_MINUTES) * ROW_HEIGHT - 4;
            return {
                top: top + 'px',
                height: height + 'px'
            };
        },
        onResDragEnd: function () {
            this.draggingResId = null;
            this.dragOverSlot = null;
        },
        onSlotDragOver: function (slot, event) {
            if (!this.canUpdateReservation) return;
            event.dataTransfer.dropEffect = 'move';
            if (this.dragOverSlot === slot) return;
            this.dragOverSlot = slot;
        },
        onSlotDragLeave: function () {
            this.dragOverSlot = null;
        },
        onSlotDrop: async function (slot, event) {
            if (!this.canUpdateReservation) return;
            this.dragOverSlot = null;
            const resId = this.draggingResId || event.dataTransfer.getData('text/plain');
            if (resId) {
                try {
                    await this.reservationStore.updateReservationTime(resId, slot);
                } catch (error) {
                    this.showActionError(error);
                }
            }
            this.draggingResId = null;
        },
        showSlotMenu: function (slot, event) {
            if (!this.canCreateReservation) return;
            event.stopPropagation();
            const rect = this.$refs.timelineBody.getBoundingClientRect();
            this.slotMenu = {
                visible: true,
                slot: slot,
                x: event.clientX - rect.left + 4,
                y: event.clientY - rect.top + 4
            };
            this.slotDismissHandler = () => { this.closeSlotMenu(); };
            document.addEventListener('click', this.slotDismissHandler, { once: true });
        },
        closeSlotMenu: function () {
            this.slotMenu.visible = false;
            if (this.slotDismissHandler) {
                document.removeEventListener('click', this.slotDismissHandler);
                this.slotDismissHandler = null;
            }
        },
        quickSeat: async function () {
            const slot = this.slotMenu.slot;
            this.closeSlotMenu();

            try {
                await this.reservationStore.createReservation({
                    tableIds: [this.table.id],
                    date: this.localDate,
                    time: slot,
                    guestName: 'Walk-in',
                    status: 'seated',
                    partySize: 2,
                    duration: 90
                });
            } catch (error) {
                this.showActionError(error);
            }
        },
        openNewReservationForm: function () {
            const slot = this.slotMenu.slot;
            this.closeSlotMenu();
            const store = this.reservationStore;
            store.resetForm();
            store.setFormField('tableIds', [this.table.id]);
            store.setFormField('date', this.localDate);
            store.setFormField('time', slot);
            router.push({ name: 'table-reservation-form' });
        }
    }
};
</script>

<style lang="sass" scoped>
@import '~@/assets/sass/variables'

$row-height: 32px
$label-width: 44px

.table-timeline-panel
    &__header
        display: flex
        justify-content: space-between
        align-items: center
        margin-bottom: 10px

    &__title
        font-size: 0.9rem

    &__seats
        color: #909399
        font-weight: normal

    &__timeline
        position: relative
        overflow-y: auto
        max-height: 420px
        border: 1px solid #ebeef5
        border-radius: 4px

    &__slot-row
        display: flex
        height: $row-height
        border-bottom: 1px solid #f0f0f0

        &--hour
            border-bottom-color: #dcdfe6

        &--now
            background: rgba(#f56c6c, 0.06)

    &__slot-label
        flex: 0 0 $label-width
        font-size: 0.65rem
        color: #909399
        padding: 2px 4px
        display: flex
        align-items: flex-start
        border-right: 1px solid #ebeef5

    &__slot-area
        flex: 1
        &--drag-over
            background: rgba($--color-primary, 0.1)

    &__reservation
        position: absolute
        left: $label-width + 4px
        right: 4px
        border-radius: 4px
        padding: 2px 6px
        overflow: hidden
        cursor: pointer
        z-index: 2
        font-size: 0.72rem

        &:hover
            opacity: 0.85

        &--pending
            background: #fdf6ec
            border: 1px solid #e6a23c
            color: #c07a00

        &--confirmed
            background: #f0f9eb
            border: 1px solid #67c23a
            color: #4a9929

        &--seated
            background: #ecf5ff
            border: 1px solid #409eff
            color: #1a7fd4

        &--completed
            background: #f4f4f5
            border: 1px solid #909399
            color: #606266

        &--cancelled
            background: #fef0f0
            border: 1px solid #f56c6c
            color: #c0392b
            opacity: 0.5
            text-decoration: line-through

    &__res-time
        font-weight: 700
        white-space: nowrap

    &__res-guest
        white-space: nowrap
        overflow: hidden
        text-overflow: ellipsis

    &__res-size
        color: inherit
        opacity: 0.7

    &__slot-menu
        position: absolute
        z-index: 100
        background: #fff
        border: 1px solid #dcdfe6
        border-radius: 4px
        box-shadow: 0 2px 8px rgba(0,0,0,0.15)
        padding: 8px 10px
        min-width: 160px

        &-header
            display: flex
            justify-content: space-between
            align-items: center
            font-size: 0.8rem
            font-weight: 600
            margin-bottom: 8px
            color: #303133

        &-actions
            display: flex
            flex-direction: column
            gap: 6px
            ::v-deep .el-button
                display: block
                width: 100%
                margin: 0
</style>
